import type { Countdown } from '@just-us/shared'
/**
 * pages/countdown-list.tsx
 *
 * List of couple countdowns with live ticking timers.
 * - Sticky header with "Add" → /countdowns/new
 * - Per-card live countdown (DD days HH:MM:SS) or "Completed" if past
 * - Delete per card → ConfirmSheet
 * - Empty state, skeleton loaders
 * - Bottom nav
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import ConfirmSheet from '../components/confirm-sheet'
import { useCountdowns, useDeleteCountdown } from '../hooks/use-countdowns'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
  isPast: boolean
}

const getTimeLeft = (targetDate: string): TimeLeft => {
  const target = new Date(`${targetDate}T00:00:00`).getTime()
  const now = Date.now()
  const diff = target - now

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true }

  const totalSec = Math.floor(diff / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60

  return { days, hours, minutes, seconds, isPast: false }
}

const pad2 = (n: number) => n.toString().padStart(2, '0')

const formatFullDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const CountdownCardSkeleton = () => (
  <div className="w-full bg-white rounded-[16px] border border-[#F9E0E8] p-4 animate-shimmer flex flex-col gap-3">
    <div className="h-[16px] rounded-full bg-[#F4C0D1] w-1/2" />
    <div className="h-[40px] rounded-[8px] bg-[#F9E0E8] w-3/4" />
    <div className="h-[12px] rounded-full bg-[#F9E0E8] w-1/3" />
  </div>
)

// ---------------------------------------------------------------------------
// Countdown Card — has its own ticker
// ---------------------------------------------------------------------------

interface CountdownCardProps {
  countdown: Countdown
  onDeleteClick: (countdown: Countdown) => void
  deleting: boolean
}

const CountdownCard = ({ countdown, onDeleteClick, deleting }: CountdownCardProps) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(countdown.target_date))

  useEffect(() => {
    if (timeLeft.isPast) return
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(countdown.target_date))
    }, 1000)
    return () => clearInterval(timer)
  }, [countdown.target_date, timeLeft.isPast])

  return (
    <div className="w-full bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-4 animate-fade-in-up">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810] flex-1 min-w-0 truncate">
          {countdown.title}
        </p>
        <button
          type="button"
          onClick={() => onDeleteClick(countdown)}
          disabled={deleting}
          className="shrink-0 w-[28px] h-[28px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[14px] active:scale-90 active:bg-[#F9E0E8] transition-all duration-150 touch-manipulation disabled:opacity-40"
          aria-label="Delete countdown"
        >
          ×
        </button>
      </div>

      {timeLeft.isPast ? (
        <div className="flex items-center gap-2">
          <span className="text-[24px]">🎉</span>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#D4537E]">
            Completed!
          </span>
        </div>
      ) : (
        <div>
          <p className="font-['Playfair_Display'] text-[36px] font-semibold text-[#D4537E] leading-none">
            {timeLeft.days}{' '}
            <span className="font-['DM_Sans'] text-[16px] font-medium text-[#B89AA2]">days</span>
          </p>
          <p className="font-['DM_Sans'] text-[14px] font-medium text-[#D4537E] tracking-tight mt-[2px]">
            {pad2(timeLeft.hours)}:{pad2(timeLeft.minutes)}:{pad2(timeLeft.seconds)}
          </p>
        </div>
      )}

      <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mt-[6px]">
        {timeLeft.isPast
          ? `Was on ${formatFullDate(countdown.target_date)}`
          : formatFullDate(countdown.target_date)}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CountdownListPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useCountdowns()
  const deleteCountdown = useDeleteCountdown()

  const [deleteTarget, setDeleteTarget] = useState<Countdown | null>(null)

  const countdowns = data?.data ?? []

  const handleDeleteClick = (countdown: Countdown) => {
    setDeleteTarget(countdown)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget) return
    deleteCountdown.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => setDeleteTarget(null),
    })
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Countdowns
          </span>
          <button
            type="button"
            onClick={() => navigate('/countdowns/new')}
            className="font-['DM_Sans'] text-[14px] font-medium text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
          >
            Add
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[88px] flex flex-col gap-3">
        {isLoading ? (
          <>
            <CountdownCardSkeleton />
            <CountdownCardSkeleton />
          </>
        ) : countdowns.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-20 gap-3 animate-fade-in">
            <div className="text-[52px] animate-float">⏳</div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              No countdowns yet
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
              Count down to your next adventure, date night, or special day.
            </p>
            <button
              type="button"
              onClick={() => navigate('/countdowns/new')}
              className="mt-2 h-[44px] px-6 rounded-full bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-95 active:bg-[#993556] transition-all duration-150 touch-manipulation"
            >
              Add your first countdown
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {countdowns.map((c) => (
              <CountdownCard
                key={c.id}
                countdown={c}
                onDeleteClick={handleDeleteClick}
                deleting={deleteCountdown.isPending}
              />
            ))}
          </div>
        )}
      </main>

      <BottomNav />

      {/* Delete Confirmation */}
      <ConfirmSheet
        open={deleteTarget !== null}
        title="Delete countdown?"
        message="This event will be removed from your list."
        confirmLabel="Delete"
        cancelLabel="Keep it"
        emoji="⏳"
        loading={deleteCountdown.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
